"use client";

// React Imports
import * as React from "react";

// External Libraries
import {Button, Menu, MenuItem, Box, Typography} from "@mui/material"

// Private Libraries
import { Badge } from "@int-traderjoes/ui-components-library";

// Contexts
import { useTheme } from "@/context";
import { useAuth } from "@/context/AuthContext";

// Services
import AuthService from "@/services/authService";

// Assets
import { LogoutIcon } from "@/assets/icons/Header/SvgUtils";



// Styles
import "./profile-menu.scss";
import { Role } from "@/types/User.types";
import { capitalizeText, getProfileDisplayName } from "@/utils/helper";


const ProfileMenu: React.FC<{isUserAuthenticated: boolean}> = ({isUserAuthenticated}) => {
  const { userDetails } = useAuth();
  const { darkMode } = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const getProfileInitials = (fullName: string) => {
    if (!fullName) return "NA"; // Default initials if no name is provided

    const nameBeforeParen = fullName.indexOf('(') > -1 ? (fullName.split('(')?.[0] ?? '') : fullName;
    const nameParts = nameBeforeParen.trim().split(" ");

    if (nameParts.length === 1) {
      // If it's a single word, take the first two letters
      const firstWord = nameParts?.[0] ?? "";
      return `${firstWord?.[0]?.toUpperCase() ?? ""}${firstWord?.[1]?.toUpperCase() ?? ""}` || "NA";
    }

    const firstName = nameParts?.[0] ?? ""; // Get the first part (first name)
    const lastName = nameParts?.[nameParts.length - 1] ?? ""; // Get the last part (last name)

    const initials = `${firstName?.[0]?.toUpperCase() ?? ""}${lastName?.[0]?.toUpperCase() ?? ""}`; // Combine initials of first and last name
    return initials || "NA"; // Return initials or a default value if no valid initials
  };

  return (
    <div>
      <Button
        id="basic-button"
        className="profile-menu-btn"
        aria-controls={open ? "profile-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        <Badge
          text={getProfileInitials(userDetails?.username || '')}
          textColor="#000000"
          backgroundColor={darkMode?"#A5684D":"#A5684D73"}
          className="profile-icon"
        />
      </Button>
      <Menu
        id="profile-menu"
        className="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: darkMode ? "#2c2c2c" : "#fff",
          },
        }}
      >
        {isUserAuthenticated && 
        <MenuItem onClick={handleClose} sx={{ alignItems: "flex-start"}}>
          <Badge
            text={getProfileInitials(userDetails?.username || '')}
            textColor="#000000"
            backgroundColor={darkMode?"#A5684D":"#A5684D73"}
            className="profile-icon"
          />
          <Box>
            <Typography className="user-name">
              {getProfileDisplayName(userDetails?.username || '') || "NA"}
            </Typography>
            <Typography className="user-role"
             sx={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              maxWidth: "285px"
            }}>
              {userDetails?.roles
                ?.map(({ rolename }: Role) => {
                  const roleName = rolename?.split("|")?.[0] ?? "";
                  return capitalizeText(roleName.trim());
                })
                .filter(Boolean)
                .join(", ") || "No roles"}
            </Typography>
          </Box>
        </MenuItem>
        }
        <MenuItem onClick={AuthService.signOut} className="logout">
          {LogoutIcon(darkMode ? "dark" : "light")} Logout
        </MenuItem>
      </Menu>
    </div>
  );
};
export default ProfileMenu;